import React from 'react';
import {IQuantityInputProps, IQuantityInputState} from '../../../types';
import {isNotNullOrUndefined} from '../../../utils/runtimeUtils';
import Translation from '../../Translation';
import {InputType} from '../index';


type Props = IQuantityInputProps;
type State = IQuantityInputState;

const COMMIT_DELAY_MS = 1000;


class QuantityInput extends React.Component<Props, State> {
    private commitTimeout: null | ReturnType<typeof setTimeout> = null;

    constructor(props: Props) {
        super(props);
        this.state = {
            draftValue: null,
        };
    }

    componentWillUnmount() {
        this.clearCommitTimeout();
    }

    render() {
        const minValue = this.props.minValue || 0;
        const displayValue = null !== this.state.draftValue ? this.state.draftValue : this.props.value || minValue;

        return (
            <div className={`custom-number-input-wrapper ${this.props.disabled ? 'disabled' : ''} `}>
                <button className="value-handler-button decrement"
                        onClick={(e: any) => this.decrementValue(e)}>
                    <span className="sr-only">
                        <Translation text={'button.srDecrementButton'}/>
                    </span>
                </button>
                <input max={this.props.maxValue}
                       min={minValue}
                       step={this.props.stepValue}
                       value={displayValue}
                       onChange={(e) => this.onChange(e)}
                       onBlur={() => this.commitDraft()}
                       onKeyDown={(e) => this.onKeyDown(e)}
                       name={this.props.name}
                       disabled={this.props.disabled}
                       type={InputType.NUMBER}
                       className={`${this.props.inputStyles} custom-number-input`}/>
                <button className="value-handler-button increment"
                        onClick={(e: any) => this.incrementValue(e)}>
                    <span className="sr-only">
                        <Translation text={'button.srIncrementButton'}/>
                    </span>
                </button>
            </div>
        );
    }

    private incrementValue = (e: any) => {
        e.preventDefault();
        if (true === this.props.disabled) {
            return;
        }
        const value = this.commitDraft();

        if (this.isSafeNumber(this.props.maxValue) && this.props.maxValue <= value) {
            return;
        }

        this.props.handleChange(this.normalize(value + this.step));
    };

    private decrementValue = (e) => {
        e.preventDefault();
        if (true === this.props.disabled) {
            return;
        }
        const value = this.commitDraft();

        if (this.isSafeNumber(this.props.minValue) && this.props.minValue >= value) {
            return;
        }

        this.props.handleChange(this.normalize(value - this.step));
    };

    private onChange = (e: any) => {
        this.setState({draftValue: e.target.value});
        this.clearCommitTimeout();
        this.commitTimeout = setTimeout(() => this.commitDraft(), COMMIT_DELAY_MS);
    };

    private onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if ('Enter' === e.key) {
            this.commitDraft();
        }
    };

    /**
     * Applies the typed value (rounded up to the step and clamped to min/max) and returns the resulting value.
     * An empty or invalid draft is discarded and the previous value is kept.
     */
    private commitDraft = (): number => {
        this.clearCommitTimeout();
        const draftValue = this.state.draftValue;
        if (null === draftValue) {
            return this.value;
        }

        this.setState({draftValue: null});
        const parsed = Number(draftValue);
        if ('' === draftValue.trim() || !Number.isFinite(parsed)) {
            return this.value;
        }

        const normalized = this.normalize(parsed);
        if (normalized !== this.props.value) {
            this.props.handleChange(normalized);
        }

        return normalized;
    };

    private normalize(value: number): number {
        let normalized = Math.ceil(value / this.step) * this.step;

        if (this.isSafeNumber(this.props.maxValue) && normalized > this.props.maxValue) {
            normalized = this.props.maxValue;
        }
        if (this.isSafeNumber(this.props.minValue) && normalized < this.props.minValue) {
            normalized = this.props.minValue;
        }

        return normalized;
    }

    private clearCommitTimeout() {
        if (null !== this.commitTimeout) {
            clearTimeout(this.commitTimeout);
            this.commitTimeout = null;
        }
    }

    private get step(): number {
        return this.props.stepValue ? this.props.stepValue : 1;
    }

    private get value() {
        if (this.isSafeNumber(this.props.value)) {
            return this.props.value;
        }
        if (this.isSafeNumber(this.props.minValue)) {
            return this.props.minValue;
        }
        if (this.isSafeNumber(this.props.maxValue)) {
            return this.props.maxValue;
        }

        return 0;
    }

    private isSafeNumber(val: any): boolean {
        return isNotNullOrUndefined(val) && !Number.isNaN(val) && Number.isSafeInteger(val);
    }
}

export default QuantityInput;
